/**
 * Portal scope enforcement.
 *
 * Every portal that serves the shared Activity / AI-log contract does so under
 * a fixed scope: Cascade only ever serves Cascade's `client_id`, MPF only
 * MPF's. This module is the single place that scope is applied, so "a filter
 * cannot widen what a portal can see" is one property proved once rather than
 * an invariant re-implemented in three Workers.
 *
 * The rule is one line and it matters: scope keys are spread LAST and
 * UNCONDITIONALLY. A request carrying `clientId=some-other-org` does not merge
 * with the scope, it is overwritten by it. There is no negotiation, no
 * "only if absent", and no per-key exception — those are the shapes that turn
 * into a tenancy bug the first time someone adds a filter.
 *
 * This is the server's job. The viewers apply the same rule client-side, but
 * that is defence in depth: a portal that only ever queries its own client_id
 * cannot leak another organization's rows however the browser asks.
 */

import type { ActivityQueryParams, AiLogQueryParams } from './api-contract.js';

/**
 * The subset of query parameters a portal may pin.
 *
 * Deliberately narrow. A portal fixes WHO the log is about (organization, app,
 * portal) and WHAT SENSITIVITY it may show (visibility) — not what the operator
 * is looking for. Pinning `search` or `category` would be a portal deciding the
 * user's question for them, which is a product choice, not a boundary.
 */
export type ActivityScope = Pick<
  ActivityQueryParams,
  | 'clientId'
  | 'appId'
  | 'portal'
  | 'visibility'
  | 'stream'
  | 'entityType'
  | 'entityId'
  | 'actorId'
  | 'actorEmail'
  | 'switchKey'
  | 'switchExecutionId'
  | 'correlationId'
>;

export type AiLogScope = Pick<
  AiLogQueryParams,
  | 'clientId'
  | 'appId'
  | 'portal'
  | 'visibility'
  | 'actorId'
  | 'actorEmail'
  | 'switchKey'
  | 'switchExecutionId'
  | 'correlationId'
>;

/**
 * Narrow `params` by `scope`. Scope always wins.
 *
 * Undefined scope values are stripped first, so `{ clientId: undefined }` does
 * not blank out a legitimate request filter — an absent pin is not a pin to
 * nothing.
 */
export function applyActivityScope(
  params: ActivityQueryParams,
  scope: ActivityScope | undefined,
): ActivityQueryParams {
  return { ...params, ...definedOnly(scope) };
}

export function applyAiLogScope(
  params: AiLogQueryParams,
  scope: AiLogScope | undefined,
): AiLogQueryParams {
  return { ...params, ...definedOnly(scope) };
}

function definedOnly<T extends object>(scope: T | undefined): Partial<T> {
  if (!scope) return {};
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(scope)) {
    if (value !== undefined && value !== null && value !== '') out[key] = value;
  }
  return out as Partial<T>;
}
